import { TypedDocumentNode, gql } from '@apollo/client';
import { addMonths } from 'date-fns';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { Matches as DumbMatches } from '@sorare/gameplay/src/components/seo/Matches';
import { anyGameToGenericGame } from '@sorare/gameplay/src/lib/anyGame';

import { useLayoutData } from 'pages/countries/$countryCode/layout';
import { countryMessages } from 'pages/seo/countries/utils';

import {
  NationalTeamFutureFixturesQuery,
  NationalTeamFutureFixturesQueryVariables,
  NationalTeamPastFixturesQuery,
  NationalTeamPastFixturesQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const NATIONAL_TEAM_PAST_FIXTURES_QUERY = gql`
  query NationalTeamPastFixturesQuery($countryCode: String!, $cursor: String) {
    football {
      nationalTeam(countryCode: $countryCode) {
        slug
        pastGames: latestGames(first: 10, includingLive: true, after: $cursor) {
          nodes {
            id
            ...anyGameToGenericGame_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${anyGameToGenericGame.fragments.anyGameInterface}
` as TypedDocumentNode<
  NationalTeamPastFixturesQuery,
  NationalTeamPastFixturesQueryVariables
>;

const NATIONAL_TEAM_FUTURE_FIXTURES_QUERY = gql`
  query NationalTeamFutureFixturesQuery(
    $countryCode: String!
    $startDate: ISO8601DateTime!
    $endDate: ISO8601DateTime!
    $cursor: String
  ) {
    football {
      nationalTeam(countryCode: $countryCode) {
        slug
        futureGames: games(
          startDate: $startDate
          endDate: $endDate
          first: 10
          after: $cursor
        ) {
          nodes {
            id
            ...anyGameToGenericGame_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${anyGameToGenericGame.fragments.anyGameInterface}
` as TypedDocumentNode<
  NationalTeamFutureFixturesQuery,
  NationalTeamFutureFixturesQueryVariables
>;

const params = {
  startDate: new Date(),
  endDate: addMonths(new Date(), 2),
};

const Fixtures = () => {
  const navigate = useNavigate();
  const generateDialogPath = useGenerateDialogPath();
  const { country } = useLayoutData();
  const { formatMessage } = useIntl();

  const displayName = formatMessage(countryMessages.nationalTeam, {
    country: country.name,
  });

  const { countryCode } = useTypedParams<PageParams>();

  useTitleAndDescription(
    metadatas.clubFixtures.title,
    metadatas.clubFixtures.description,
    {
      club: displayName,
    }
  );

  const {
    data: dataFuture,
    loading: loadingFuture,
    loadMore: loadMoreFuture,
  } = usePaginatedQuery(NATIONAL_TEAM_FUTURE_FIXTURES_QUERY, {
    variables: {
      countryCode: countryCode || '',
      ...params,
    },
    connection: 'GameConnection',
    skip: !countryCode,
  });

  const { InfiniteScrollLoader: InfiniteScrollLoaderFuture } =
    useInfiniteScroll(
      () => {
        loadMoreFuture(false, {
          cursor:
            dataFuture?.football.nationalTeam.futureGames.pageInfo.endCursor,
        });
      },
      !!dataFuture?.football.nationalTeam.futureGames.pageInfo.hasNextPage,
      loadingFuture
    );

  const {
    data: dataPast,
    loading: loadingPast,
    loadMore: loadMorePast,
  } = usePaginatedQuery(NATIONAL_TEAM_PAST_FIXTURES_QUERY, {
    variables: { countryCode: countryCode || '' },
    connection: 'GameConnection',
    skip: !countryCode,
  });

  const { InfiniteScrollLoader: InfiniteScrollLoaderPast } = useInfiniteScroll(
    () => {
      setTimeout(() => {
        loadMorePast(false, {
          cursor: dataPast?.football.nationalTeam.pastGames.pageInfo.endCursor,
        });
      }, 500);
    },
    !!dataPast?.football.nationalTeam.pastGames.pageInfo.hasNextPage,
    loadingPast
  );

  const pastGames = [
    ...(dataPast?.football.nationalTeam.pastGames.nodes || []),
  ].reverse();
  const futureGames = dataFuture?.football.nationalTeam.futureGames.nodes || [];

  return (
    <DumbMatches
      displayName={displayName}
      loading={loadingPast || loadingFuture}
      pastGames={pastGames.map(anyGame =>
        anyGameToGenericGame({
          anyGame,
          onClick: id => navigate(generateDialogPath(DialogKey.game, id)),
        })
      )}
      infiniteScrollLoaderPast={<InfiniteScrollLoaderPast />}
      futureGames={futureGames.map(anyGame =>
        anyGameToGenericGame({ anyGame })
      )}
      infiniteScrollLoaderFuture={<InfiniteScrollLoaderFuture />}
    />
  );
};

export default Fixtures;
